/*
 * Jira Ticket:
 * Created Date: Wed, 25th Mar 2020, 09:55:48 am
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { useEffect } from 'react';
import {
  List,
  Title,
  Datagrid,
  fetchEnd,
  DateField,
  TextField,
  useNotify,
  fetchStart,
  useRefresh,
  NumberField,
  FunctionField,
  useDataProvider,
} from 'react-admin';
import moment from 'moment';
import { useDispatch } from 'react-redux';

import ListActions from './actions';
import ListFilters from './filters';
import { downloadFile } from 'utils';
import { inspectionStatusField } from 'styles';
import { red, green, amber, darkenedAmber } from 'theme';
import { usePersistentOrganisation } from '../../Components/Contexts';
import { towerStatusOptions, inspectionStatusOptions } from 'utils/choices';
import { PostPagination } from '../../Components/CustomPagination/pagination';

const MINUTE_MS = 60000; // 1 minute

const today = moment()
  .endOf('day')
  .toDate();

const oneMonthAgo = moment()
  .subtract(1, 'months')
  .startOf('day')
  .toDate();

const InspectionStatusField = ({ record }) => {
  let color;
  let status = record.cmsStatus;
  const isExpiryImminent = record.isExpiryImminent;

  switch (status) {
    case 'APPROVED':
      color = isExpiryImminent ? darkenedAmber : green;
      break;
    case 'DO_NOT_USE':
      color = red;
      break;
    case 'INSPECTED':
      color = isExpiryImminent ? darkenedAmber : amber;
      break;
    case 'REQUIRES_REINSPECTION':
      color = red;
      break;
    default:
      color = amber;
      break;
  }

  const option = inspectionStatusOptions.find(option => option.id === status);
  const classes = inspectionStatusField(color);
  return <span className={classes.root}>{option?.name}</span>;
};

const InspectionList = props => {
  const notify = useNotify();
  const refresh = useRefresh();
  const dispatch = useDispatch();
  const dataProvider = useDataProvider();
  const {
    persistentOrganisation,
    onSelectPersistentOrganisation,
  } = usePersistentOrganisation();

  // const [organisationSelected, onSelectPersistentOrganisationSelected] = useState(false);

  const exportCSV = filterValues => {
    dispatch(fetchStart());
    return dataProvider('EXPORT', 'Inspection', {
      input: { to: filterValues.dateTo, from: filterValues.dateFrom },
    })
      .then(result => {
        dispatch(fetchEnd());
        if (result.data.downloadUrl) {
          return downloadFile({
            uri: result.data.downloadUrl,
            filename: `Inspection Report (${moment(
              filterValues.dateFrom
            ).format('LL')} - ${moment(filterValues.dateTo).format('LL')}).csv`,
          });
        }
        return notify('error.noPdf', 'warning');
      })
      .catch(error => {
        dispatch(fetchEnd());
        const {
          extensions: { code },
          message,
        } = error.graphQLErrors[0];
        if (code !== 'INTERNAL_SERVER_ERROR') {
          return notify(message, 'warning');
        }
        return notify('error.generic', 'warning');
      });
  };

  useEffect(() => {
    // if(organisationId) {
    //   onSelectPersistentOrganisationSelected(true);
    // }
    const interval = setInterval(() => {
      refresh(); // refresh the insepction list once per minute CR request [PAS-832]
    }, MINUTE_MS);

    return () => clearInterval(interval); // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
  }, [refresh]);

  return (
    <>
      <Title title={`resources.General.cmsTitle`} />
      <List
        bulkActionButtons={false}
        actions={<ListActions onClick={exportCSV} />}
        filters={
          <ListFilters organisationFilter={onSelectPersistentOrganisation} />
        }
        filterDefaultValues={{
          dateFrom: oneMonthAgo,
          dateTo: today,
          organisationId: persistentOrganisation.id,
        }}
        perPage={25}
        pagination={<PostPagination />}
        {...props}
      >
        <Datagrid rowClick="edit">
          <TextField
            source="tower.id"
            sortable={false}
            label="resources.Inspection.fields.towerId"
          />
          <TextField
            source="tower.site.project.organisation.name"
            sortable={false}
            label="resources.Inspection.fields.organisation"
          />
          <TextField
            source="tower.site.project.name"
            sortable={false}
            label="resources.Inspection.fields.project"
          />
          <FunctionField
            render={record =>
              record?.tower?.site
                ? record.tower.site.name
                : record.tower.description
            }
            sortable={false}
            label="resources.Inspection.fields.site"
          />
          <NumberField
            source="inspectionNumber"
            sortable={false}
            label="resources.Inspection.fields.inspectionNo"
          />
          <InspectionStatusField
            sortable={false}
            label="resources.Inspection.fields.inspectionStatus"
            {...props}
          />
          <DateField
            source="inspectedAt"
            showTime={true}
            options={{
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              hour12: false,
              minute: '2-digit',
            }}
            sortable={false}
            label="resources.Inspection.fields.date"
          />
          <FunctionField
            label="resources.Inspection.fields.towerStatus"
            render={record => {
              let status = record.tower.cmsStatus;
              const option =
                towerStatusOptions.find(option => option.id === status) || {};
              return option.name;
            }}
          />
        </Datagrid>
      </List>
    </>
  );
};

export default InspectionList;
